/**
 * Admin › What's new: every update, with write, edit, publish and unpublish.
 * Logic lives in `~/lib/server/whats-new`.
 *
 * Aliases: admin whats new, publish update, unpublish update
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  whatsNewEntryInputSchema,
  whatsNewEntryUpdateSchema,
} from "~/lib/shared/whats-new";

const EntryIdSchema = z.object({ id: z.string().uuid() });

export const getAdminWhatsNewEntriesFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("whats-new:read")])
  .handler(async () => {
    const { listAllWhatsNewEntries } = await import("~/lib/server/whats-new");
    return listAllWhatsNewEntries();
  });

export const adminWhatsNewEntriesQueryOptions = queryOptions({
  queryKey: ["admin-whats-new", "entries"] as const,
  queryFn: () => getAdminWhatsNewEntriesFn(),
});

export const createWhatsNewEntryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("whats-new:write")])
  .validator(whatsNewEntryInputSchema)
  .handler(async ({ data }) => {
    const { createWhatsNewEntry } = await import("~/lib/server/whats-new");
    return createWhatsNewEntry(data);
  });

export const updateWhatsNewEntryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("whats-new:write")])
  .validator(whatsNewEntryUpdateSchema)
  .handler(async ({ data }) => {
    const { updateWhatsNewEntry } = await import("~/lib/server/whats-new");
    return updateWhatsNewEntry(data);
  });

export const publishWhatsNewEntryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("whats-new:write")])
  .validator(EntryIdSchema)
  .handler(async ({ data }) => {
    const { publishWhatsNewEntry } = await import("~/lib/server/whats-new");
    return publishWhatsNewEntry(data.id, new Date());
  });

export const unpublishWhatsNewEntryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("whats-new:write")])
  .validator(EntryIdSchema)
  .handler(async ({ data }) => {
    const { unpublishWhatsNewEntry } = await import("~/lib/server/whats-new");
    return unpublishWhatsNewEntry(data.id);
  });
